// src/game/entities/Player.ts
import Phaser from "phaser";
import { GAME_CONFIG } from "../config";

export class Player extends Phaser.Physics.Arcade.Sprite {
  private speed: number = GAME_CONFIG.PLAYER_SPEED;
  private jumpSpeed: number = GAME_CONFIG.JUMP_SPEED;
  private isJumping: boolean = false;
  public playerId: string;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    texture: string,
    playerId: string = "player1"
  ) {
    super(scene, x, y, texture);

    this.playerId = playerId;

    // Add to scene and physics
    scene.add.existing(this);
    scene.physics.add.existing(this);

    // Set physics properties
    this.setBounce(0.2);
    this.setCollideWorldBounds(true);
    this.setSize(28, 40); // Adjust hitbox size

    // Create animations if they don't exist
    this.createAnimations();
  }

  private createAnimations(): void {
    const scene = this.scene;

    // Only create animations if they don't already exist
    if (!scene.anims.exists(`${this.playerId}_idle`)) {
      // Check if texture has multiple frames
      const texture = this.texture;
      const hasMultipleFrames = texture.frameTotal > 1;

      if (hasMultipleFrames) {
        // Idle animation
        scene.anims.create({
          key: `${this.playerId}_idle`,
          frames: [{ key: this.texture.key, frame: 4 }],
          frameRate: 20,
        });

        // Left movement animation
        scene.anims.create({
          key: `${this.playerId}_left`,
          frames: scene.anims.generateFrameNumbers(this.texture.key, {
            start: 0,
            end: 3,
          }),
          frameRate: 10,
          repeat: -1,
        });

        // Right movement animation
        scene.anims.create({
          key: `${this.playerId}_right`,
          frames: scene.anims.generateFrameNumbers(this.texture.key, {
            start: 5,
            end: 8,
          }),
          frameRate: 10,
          repeat: -1,
        });
      } else {
        // Single frame texture - use the same frame for all animations
        scene.anims.create({
          key: `${this.playerId}_idle`,
          frames: [{ key: this.texture.key, frame: 0 }],
          frameRate: 20,
        });

        scene.anims.create({
          key: `${this.playerId}_left`,
          frames: [{ key: this.texture.key, frame: 0 }],
          frameRate: 10,
          repeat: -1,
        });

        scene.anims.create({
          key: `${this.playerId}_right`,
          frames: [{ key: this.texture.key, frame: 0 }],
          frameRate: 10,
          repeat: -1,
        });
      }
    }
  }

  public update(keyboardInput?: any): void {
    if (!this.body) return;

    const body = this.body as Phaser.Physics.Arcade.Body;

    // Handle horizontal movement
    let isMoving = false;

    // Handle horizontal movement - check for both arrow keys and WASD
    if (keyboardInput?.left?.isDown || keyboardInput?.A?.isDown) {
      this.setVelocityX(-this.speed);
      this.anims.play(`${this.playerId}_left`, true);
      isMoving = true;
    } else if (keyboardInput?.right?.isDown || keyboardInput?.D?.isDown) {
      this.setVelocityX(this.speed);
      this.anims.play(`${this.playerId}_right`, true);
      isMoving = true;
    } else {
      this.setVelocityX(0);
    }

    // Play idle animation when not moving
    if (!isMoving) {
      this.anims.play(`${this.playerId}_idle`, true);
    }

    // Handle jumping
    const isOnGround = body.touching.down;

    if (
      (keyboardInput?.up?.isDown || keyboardInput?.W?.isDown) &&
      isOnGround &&
      !this.isJumping
    ) {
      this.setVelocityY(-this.jumpSpeed);
      this.isJumping = true;

      // Play jump sound if available
      if (this.scene.sound.get("jump")) {
        this.scene.sound.play("jump", { volume: 0.1 });
      }
    }

    // Reset jumping flag when touching ground
    if (isOnGround && this.isJumping) {
      this.isJumping = false;
    }
  }

  public getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  public setPosition(x: number, y: number): this {
    this.x = x;
    this.y = y;
    return this;
  }

  public resetToSpawn(x: number, y: number): void {
    this.setPosition(x, y);
    this.setVelocity(0, 0);
    this.isJumping = false;
  }
}
